import { useRef } from 'react'

import { Button, TextSpinner } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import useAssignTemplate, {
  Props as AssignTemplateAttributes,
} from 'modules/shift_management/hooks/useAssignTemplate'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import ApplyShiftTemplateForm from 'modules/shift_management/lib/forms/ApplyShiftTemplateForm'
import {
  ConflictsProps,
  SYMBOLIC_MIXED_VALUE,
  type Shift,
  type View,
} from 'modules/shift_management/lib/types'
import templateWeeks from 'modules/shift_management/store/api/collections/templateWeeks'
import type ShiftTemplate from 'modules/shift_management/store/api/models/ShiftTemplate'
import type TemplateWeek from 'modules/shift_management/store/api/models/TemplateWeek'

import FormContent from './FormContent'

interface FormDataReturnProps {
  location_id: number | undefined | null
  work_area_id: number | undefined | null
}

const formData = (view: View, shifts: Shift[]): FormDataReturnProps => {
  if (!view.isByAreas) {
    return { location_id: null, work_area_id: null }
  }
  const locations = [...new Set(shifts.map((shift: Shift) => shift.locationId))]
  const workAreas = [...new Set(shifts.map((shift: Shift) => shift.workAreaId))]
  return {
    location_id: locations.length > 1 ? SYMBOLIC_MIXED_VALUE : locations[0],
    work_area_id: workAreas.length > 1 ? SYMBOLIC_MIXED_VALUE : workAreas[0],
  }
}

interface Props {
  shiftTemplates: ShiftTemplate[]
  onClose: () => void
  onOpenConflictModal: () => void
  onSetConflicts: (conflicts: ConflictsProps) => void
  onSetParams: (params: AssignTemplateAttributes['params']) => void
}

const AssignTemplateSidepanel = ({
  shiftTemplates,
  onClose,
  onOpenConflictModal,
  onSetConflicts,
  onSetParams,
}: Props) => {
  const [{ view }] = useSchedule()
  const { shifts: selectedShifts, daysByRow, isEnabled: isSelectionEnabled } = useSelectionState()
  const dispatchSelection = useSelectionDispatch()

  const shifts = Object.values(selectedShifts)
  const initialAttributes = formData(view, shifts)
  const selectedEmployeeIds = Object.keys(daysByRow).map(Number)
  const form = useLazyRef(
    () =>
      new ApplyShiftTemplateForm({
        ...initialAttributes,
        employeeSelection: { type: 'manual', ids: selectedEmployeeIds },
      })
  ).current
  const selectedTemplateId = form.get('template_id').value
  const selectedWeek = form.get('template_week_position').value
  const startDate = stringToDate(form.get('start_date').value, 'backend')
  const endDate = stringToDate(form.get('end_date').value, 'backend')
  const { loading, selectableEmployeeIds } = useSelectableEmployees(view.startAt, view.endAt)
  const scrollToErrorsTimeout = useRef<ReturnType<typeof setTimeout>>()

  const arrayOfDates = Object.values(daysByRow)
  const isContinuousDateRange = (arrayOfDates: string[]): boolean => {
    const result = sortDatesSelected(arrayOfDates)
    const amountSelected = result.length ? result.length - 1 : undefined
    const diff = result[result.length - 1]?.diff(result[0], 'days')
    return amountSelected === diff
  }

  const sortDatesSelected = (arrayOfDates: string[]): moment.Moment[] => {
    return arrayOfDates.map((date) => moment(date)).sort((a, b) => a.diff(b))
  }

  const isSameStartAndEndDate = (a: string[], b: string[]) => {
    return a[0] === b[0] && a[a.length - 1] === b[b.length - 1]
  }
  const isSameDateRange = arrayOfDates.every((dates, index) => {
    if (index < arrayOfDates.length - 1) {
      const nextDates = arrayOfDates[index + 1]
      return (
        dates &&
        nextDates &&
        isContinuousDateRange(dates) &&
        isSameStartAndEndDate(dates, nextDates)
      )
    }
    return dates && isContinuousDateRange(dates)
  })

  useEffect(() => {
    const fetchTemplateWeeks = async () => {
      await templateWeeks.fetch()
    }

    fetchTemplateWeeks()
    if (isSelectionEnabled) {
      const dates = arrayOfDates[0]
      const datesSorted = dates ? sortDatesSelected(dates) : []

      // Set always the first selected date as start date.
      form.setValues({ start_date: dateToString(datesSorted[0] ?? moment(), 'backend') })

      // Set the last selected date if the date range is the same for all the employees.
      if (isSameDateRange) {
        const end_date = dateToString(datesSorted[datesSorted.length - 1] ?? moment(), 'backend')
        form.setValues({ end_date })
      }
    }
  }, [])

  const weeksByTemplate = (): TemplateWeek[] => {
    const templateField = form.get('template_id')

    return templateWeeks.filter({
      template_id: parseInt(templateField.value),
      archived: false,
    })
  }
  const params: AssignTemplateAttributes['params'] = {
    selectedTemplateId: selectedTemplateId,
    selectedWeek: selectedWeek,
    startDate: startDate,
    endDate: endDate,
    notes: form.get('notes').value,
    locationId: form.get('location_id').value || null,
    workAreaId: form.get('work_area_id').value || null,
    employeeSelection: form.employeeSelection || { type: 'manual', ids: [] },
  }

  const { onSubmit, submitting } = useAssignTemplate({
    params,
    from: 'side_panel',
    onSuccess: (noConflicts: boolean, conflicts: ConflictsProps) => {
      if (noConflicts) {
        onClose()
      } else {
        onClose()
        onSetConflicts(conflicts)
        onSetParams(params)
        onOpenConflictModal()
      }
      dispatchSelection({
        type: 'CLEAR_SELECTION',
      })
    },
  })

  const handleErrors = (): boolean => {
    return form.validate() && !!weeksByTemplate().length
  }

  const scrollToErrors = () => {
    // Timeout to wait for the error to be rendered
    clearTimeout(scrollToErrorsTimeout.current)
    scrollToErrorsTimeout.current = setTimeout(() => {
      const errorAnchors = document.getElementsByClassName('error-anchor')
      const firstError = errorAnchors[0]
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 100)
  }

  const handleSubmit = () => {
    const isValid = handleErrors()
    if (!isValid) return scrollToErrors()
    onSubmit()
  }

  return (
    <Sidepanel
      open
      title={i18n.t('shift_management.actions.add_rotating_shift')}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} submit>
            {i18n.t('common.add')}
          </Button>
        </>
      }
      onClose={onClose}
      closeLabel="Close"
    >
      {loading ? (
        <Loading />
      ) : (
        <>
          {submitting && (
            <TextSpinner text={i18n.t('shift_management.actions.applying_rotating_shift')} />
          )}
          {!submitting && (
            <FormContent
              form={form}
              shiftTemplates={shiftTemplates}
              weeksByTemplate={weeksByTemplate}
              selectableEmployeeIds={selectableEmployeeIds}
              mixedValuesByLocation={initialAttributes.location_id === SYMBOLIC_MIXED_VALUE}
              mixedValuesByWorkArea={initialAttributes.work_area_id === SYMBOLIC_MIXED_VALUE}
            />
          )}
        </>
      )}
    </Sidepanel>
  )
}

export default observer(AssignTemplateSidepanel)
